/**
 * BotLaunchPanel — CM tab inside BulkCampaignWorkspace. Lets the campaign
 * manager review auto-generated bot questions, pick creators, tune margin +
 * batching, and launch. Launch sends the initial WhatsApp outreach via the
 * Meta Cloud API in background batches.
 *
 * Visibility: CM / Admin / AE only. Never brand / creator / talent_manager.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Bot, Sparkles, Plus, Trash2, Edit2, Check, X, Loader2, Send, Rocket,
  GripVertical, IndianRupee,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEBHOOK_URL = `${API_URL}/api/webhook`;
const WEBHOOK_VERIFY_TOKEN = 'vyvre_webhook_2026';

const WebhookInfo = () => (
  <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 space-y-2" data-testid="bot-webhook-info">
    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">Meta webhook setup</div>
    <div className="text-sm text-[#0F0F0F] dark:text-white">
      WhatsApp Webhook URL: <code className="font-mono text-xs bg-black/[0.04] dark:bg-white/5 px-1.5 py-0.5 rounded select-all" data-testid="bot-webhook-url">{WEBHOOK_URL}</code>
    </div>
    <div className="text-sm text-[#0F0F0F] dark:text-white">
      Verify Token: <code className="font-mono text-xs bg-black/[0.04] dark:bg-white/5 px-1.5 py-0.5 rounded select-all" data-testid="bot-webhook-verify-token">{WEBHOOK_VERIFY_TOKEN}</code>
    </div>
    <p className="text-xs text-[#6B6B6B] dark:text-zinc-400">
      Configure these in Meta Developer Console → WhatsApp → Configuration → Webhook
    </p>
  </div>
);

const SOURCE_META = {
  template:     { label: 'Template',     color: '#00C972' },
  ai_generated: { label: 'AI Generated', color: '#0055FF' },
  manual:       { label: 'Manual',       color: '#71717a' },
};

const BOT_STATUS_META = {
  not_started: { label: 'Not Started', color: '#71717a' },
  in_progress: { label: 'In Progress', color: '#0055FF' },
  completed:   { label: 'Completed',   color: '#00C972' },
  failed:      { label: 'Failed',      color: '#FF3B30' },
};

export const BotLaunchPanel = ({ campaignId, creators = [], onReload }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [marginPct, setMarginPct] = useState(20);
  const [batchSize, setBatchSize] = useState(20);
  const [batchDelay, setBatchDelay] = useState(30);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_URL}/api/campaigns/${campaignId}/bot-config`,
        { withCredentials: true },
      );
      setConfig(data);
      if (data?.default_margin_percent) setMarginPct(data.default_margin_percent);
      if (data?.launch_settings?.batch_size) setBatchSize(data.launch_settings.batch_size);
      if (data?.launch_settings?.batch_delay_minutes) setBatchDelay(data.launch_settings.batch_delay_minutes);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bot config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (campaignId) loadConfig(); /* eslint-disable-next-line */ }, [campaignId]);

  // Default: pre-select every creator so the CM can uncheck rather than click 200.
  useEffect(() => {
    if (creators.length > 0 && selectedIds.size === 0) {
      setSelectedIds(new Set(creators.map((c) => c.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creators.length]);

  const questions = config?.questions || [];
  const isLaunched = config?.status === 'launched';

  const saveConfig = async (nextQuestions, extra = {}) => {
    try {
      await axios.post(
        `${API_URL}/api/campaigns/${campaignId}/bot-config`,
        {
          questions: nextQuestions,
          cost_question_enabled: config?.cost_question_enabled ?? true,
          cost_suggestion_text: config?.cost_suggestion_text,
          default_margin_percent: extra.default_margin_percent ?? marginPct,
        },
        { withCredentials: true },
      );
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/campaigns/${campaignId}/bot-config/generate`,
        {},
        { withCredentials: true },
      );
      setConfig(data);
      if (data?.llm_note) {
        toast.message(data.llm_note);
      } else {
        toast.success(`Generated ${data.matched_count ?? (data.questions || []).length} question(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const addQuestion = async () => {
    const text = newQuestion.trim();
    if (!text) return;
    const next = [
      ...questions,
      {
        question_id: null,
        question_text: text,
        source: 'manual',
        template_id: null,
        mandatory: true,
        order: questions.length,
      },
    ];
    setNewQuestion('');
    setAddingQuestion(false);
    await saveConfig(next);
  };

  const saveEdit = async (idx) => {
    const text = editText.trim();
    if (!text) return;
    const next = questions.map((q, i) => (i === idx ? { ...q, question_text: text } : q));
    setEditingIdx(null);
    setEditText('');
    await saveConfig(next);
  };

  const deleteQuestion = async (idx) => {
    const next = questions.filter((_, i) => i !== idx);
    await saveConfig(next);
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (targetIdx) => {
    if (dragIdx == null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const next = [...questions];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setDragIdx(null);
    await saveConfig(next.map((q, i) => ({ ...q, order: i })));
  };

  const toggleCreator = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === creators.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(creators.map((c) => c.id)));
  };

  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/campaigns/${campaignId}/bot-config/launch`,
        {
          creator_ids: Array.from(selectedIds),
          batch_size: batchSize,
          batch_delay_minutes: batchDelay,
          default_margin_percent: marginPct,
        },
        { withCredentials: true },
      );
      toast.success(data.message || 'Bot campaign launched');
      setConfirmOpen(false);
      await loadConfig();
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Launch failed');
    } finally {
      setLaunching(false);
    }
  };

  const batchCount = useMemo(
    () => Math.max(1, Math.ceil(selectedIds.size / Math.max(1, batchSize))),
    [selectedIds.size, batchSize],
  );

  const canLaunch = questions.length > 0 && selectedIds.size > 0 && !isLaunched;

  return (
    <div className="space-y-6" data-testid="bot-launch-panel">
      {isLaunched && (
        <div className="bg-[#00C972]/10 border border-[#00C972]/30 text-[#00C972] rounded-2xl p-4 flex items-center gap-2 text-sm" data-testid="bot-launched-banner">
          <Rocket size={16} /> Bot campaign launched. WhatsApp outreach is sending via Meta in background batches.
        </div>
      )}

      {/* ── SECTION 1: Questions ─────────────────────────────────────── */}
      <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid="bot-questions-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-2">
              <Bot size={18} className="text-[#FF451A]" /> Campaign Questions
            </h3>
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5">
              Generated from campaign mandates. Review and edit before launching.
            </p>
          </div>
          <Button
            onClick={generate}
            disabled={generating || isLaunched}
            className="bg-[#FF451A] text-white rounded-full px-4 py-2 hover:bg-[#E63E17] flex items-center gap-1.5 disabled:opacity-40"
            data-testid="bot-generate-button"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Generate Questions
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-6 text-[#6B6B6B] dark:text-zinc-400 flex items-center justify-center gap-2 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : questions.length === 0 ? (
          <div className="border border-dashed border-black/[0.10] dark:border-white/10 rounded-xl p-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-400" data-testid="bot-questions-empty">
            No questions yet. Click <span className="font-semibold text-[#FF451A]">Generate Questions</span> to auto-derive them from campaign mandates, or add a custom one below.
          </div>
        ) : (
          <div className="space-y-2" data-testid="bot-questions-list">
            {questions.map((q, i) => {
              const meta = SOURCE_META[q.source] || SOURCE_META.manual;
              const editing = editingIdx === i;
              return (
                <div
                  key={q.question_id || i}
                  draggable={!editing && !isLaunched}
                  onDragStart={() => onDragStart(i)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(i)}
                  className={`bg-zinc-50 dark:bg-white/[0.03] border border-black/[0.06] dark:border-white/5 rounded-xl p-3 flex items-start gap-3 ${
                    dragIdx === i ? 'opacity-40' : ''
                  }`}
                  data-testid={`bot-question-${i}`}
                >
                  <div className="flex items-center gap-2 text-[#6B6B6B] dark:text-zinc-500 shrink-0 pt-1">
                    {!isLaunched && <GripVertical size={14} className="cursor-grab" />}
                    <span className="text-xs font-mono">{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-mono border"
                        style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}14` }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    {editing ? (
                      <div className="flex gap-1.5">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={2}
                          className="flex-1 rounded-lg text-sm p-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                          data-testid={`bot-question-edit-input-${i}`}
                        />
                        <div className="flex flex-col gap-1">
                          <button onClick={() => saveEdit(i)} className="p-1.5 rounded-lg bg-[#00C972]/10 text-[#00C972] hover:bg-[#00C972]/20" data-testid={`bot-question-save-${i}`}><Check size={13} /></button>
                          <button onClick={() => { setEditingIdx(null); setEditText(''); }} className="p-1.5 rounded-lg bg-black/5 dark:bg-white/5 text-[#6B6B6B] dark:text-zinc-400"><X size={13} /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-[#0F0F0F] dark:text-white leading-relaxed">
                        {q.question_text}
                      </div>
                    )}
                  </div>
                  {!editing && !isLaunched && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => { setEditingIdx(i); setEditText(q.question_text); }}
                        className="p-1.5 text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A]"
                        data-testid={`bot-question-edit-${i}`}
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => deleteQuestion(i)}
                        className="p-1.5 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30]"
                        data-testid={`bot-question-delete-${i}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add custom */}
        {!isLaunched && (
          <div className="mt-3">
            {addingQuestion ? (
              <div className="flex gap-1.5">
                <Input
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addQuestion(); }}
                  autoFocus
                  placeholder="Type a question the bot should ask…"
                  className="rounded-xl text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="bot-question-add-input"
                />
                <Button onClick={addQuestion} className="bg-[#FF451A] text-white rounded-lg px-3 hover:bg-[#E63E17]" data-testid="bot-question-add-submit">Add</Button>
                <Button onClick={() => { setAddingQuestion(false); setNewQuestion(''); }} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#6B6B6B] dark:text-zinc-400 rounded-lg px-3">Cancel</Button>
              </div>
            ) : (
              <button
                onClick={() => setAddingQuestion(true)}
                className="w-full text-left px-3 py-2 rounded-xl border border-dashed border-black/[0.15] dark:border-white/15 text-sm text-[#FF451A] hover:bg-[#FF451A]/5 flex items-center gap-1.5 font-medium"
                data-testid="bot-question-add-toggle"
              >
                <Plus size={14} /> Add Custom Question
              </button>
            )}
          </div>
        )}

        {/* Cost question (locked at the bottom) */}
        <div className="mt-4 pt-4 border-t border-black/[0.06] dark:border-white/5" data-testid="bot-cost-question">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee size={14} className="text-[#00C972]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#00C972]">
              Cost Confirmation · Always Asked Last
            </span>
          </div>
          <div className="bg-[#00C972]/5 border border-[#00C972]/20 rounded-xl p-3 text-sm text-[#0F0F0F] dark:text-white">
            {config?.cost_suggestion_text || 'For this campaign, what is your rate? Reply YES to confirm or suggest your rate.'}
          </div>
        </div>
      </div>

      {/* ── SECTION 2: Creator selection ────────────────────────────── */}
      <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid="bot-creators-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white">Select Creators to Message</h3>
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5">
              <span className="font-mono text-[#FF451A]">{selectedIds.size}</span> of {creators.length} selected
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-[#0F0F0F] dark:text-white">
            <input
              type="checkbox"
              checked={selectedIds.size === creators.length && creators.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 accent-[#FF451A]"
              data-testid="bot-select-all"
            />
            Select all
          </label>
        </div>

        {creators.length === 0 ? (
          <div className="border border-dashed border-black/[0.10] dark:border-white/10 rounded-xl p-6 text-center text-sm text-[#6B6B6B] dark:text-zinc-400">
            No creators in this campaign yet.
          </div>
        ) : (
          <div className="divide-y divide-black/[0.06] dark:divide-white/5 max-h-96 overflow-y-auto" data-testid="bot-creators-list">
            {creators.map((c) => {
              const meta = BOT_STATUS_META[c.bot_status || 'not_started'];
              const clientCost = c.client_cost_total;
              const costLabel = clientCost != null
                ? `Cost Confirmed ₹${Number(clientCost).toLocaleString('en-IN')}`
                : 'Cost Pending';
              const selected = selectedIds.has(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-3 py-2 cursor-pointer"
                  data-testid={`bot-creator-row-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleCreator(c.id)}
                    disabled={isLaunched}
                    className="w-4 h-4 accent-[#FF451A]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">
                      {c.creator_name || '—'}
                    </div>
                    <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
                      {c.instagram_handle ? `@${c.instagram_handle} · ` : ''}
                      {c.instagram_followers != null ? `${Number(c.instagram_followers).toLocaleString('en-IN')} followers` : ''}
                    </div>
                  </div>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-mono border shrink-0"
                    style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}14` }}
                  >
                    {meta.label}
                  </span>
                  <span
                    className={`text-[11px] font-mono shrink-0 ${
                      clientCost != null ? 'text-[#00C972]' : 'text-[#FFB800]'
                    }`}
                  >
                    {costLabel}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SECTION 3: Launch settings ─────────────────────────────── */}
      <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid="bot-settings-section">
        <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-3">Launch Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <NumField label="Default Margin (%)" value={marginPct} onChange={setMarginPct} min={0} testid="bot-margin-input" disabled={isLaunched} />
          <NumField label="Batch Size" value={batchSize} onChange={setBatchSize} min={1} testid="bot-batch-size" disabled={isLaunched} />
          <NumField label="Batch Delay (minutes)" value={batchDelay} onChange={setBatchDelay} min={0} testid="bot-batch-delay" disabled={isLaunched} />
        </div>
      </div>

      {/* ── SECTION 4: Launch button ───────────────────────────────── */}
      <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid="bot-launch-section">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!canLaunch}
          className="w-full bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-2xl py-4 text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="bot-launch-button"
        >
          <Rocket size={16} />
          {isLaunched ? 'Bot Campaign Launched' : 'Launch Bot Campaign'}
        </Button>
        {!isLaunched && (
          <p className="text-xs text-center text-[#6B6B6B] dark:text-zinc-500 mt-2 font-mono">
            Send to <span className="text-[#FF451A] font-semibold">{selectedIds.size}</span> creators in <span className="text-[#FF451A] font-semibold">{batchCount}</span> batch{batchCount === 1 ? '' : 'es'}
          </p>
        )}
      </div>

      <WebhookInfo />

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="bot-launch-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Launch Bot Campaign?</DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400 leading-relaxed pt-2">
              This will send WhatsApp messages to <span className="font-semibold text-[#0F0F0F] dark:text-white">{selectedIds.size} creators</span>.
              Messages will be sent in batches of <span className="font-semibold text-[#0F0F0F] dark:text-white">{batchSize}</span> every <span className="font-semibold text-[#0F0F0F] dark:text-white">{batchDelay} minutes</span>. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2">
            <Button
              onClick={() => setConfirmOpen(false)}
              disabled={launching}
              className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-full px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={launch}
              disabled={launching}
              className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-4 flex items-center gap-1.5 disabled:opacity-40"
              data-testid="bot-launch-confirm-button"
            >
              {launching ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Launch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NumField = ({ label, value, onChange, min = 0, testid, disabled }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-[0.08em] block mb-1.5 font-mono text-[#0F0F0F] dark:text-white">
      {label}
    </label>
    <Input
      type="number"
      value={value}
      min={min}
      disabled={disabled}
      onChange={(e) => onChange(Math.max(min, Number(e.target.value) || 0))}
      className="rounded-xl disabled:opacity-60 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
      data-testid={testid}
    />
  </div>
);

export default BotLaunchPanel;
